/**
 * useVersionHistory Hook
 *
 * Manages version history for songs — loading, creating, restoring versions.
 * Works with both local (IndexedDB) and cloud (Supabase) storage.
 */
import { useState, useCallback, useRef } from "react";
import * as versionIDB from "../utils/versionIndexedDB";
import * as versionAPI from "../services/versionService";

/**
 * Compute a human-readable change summary between old and new content
 */
function computeChangeSummary(oldContent, newContent) {
  if (!oldContent) return "Initial version";

  const oldLines = oldContent.split("\n").filter((l) => l.trim());
  const newLines = newContent.split("\n").filter((l) => l.trim());
  const oldWords = oldContent.split(/\s+/).filter((w) => w.trim());
  const newWords = newContent.split(/\s+/).filter((w) => w.trim());

  const linesDiff = newLines.length - oldLines.length;
  const wordsDiff = newWords.length - oldWords.length;

  // Calculate % changed (rough)
  const maxLen = Math.max(oldContent.length, newContent.length, 1);
  let diffChars = 0;
  for (let i = 0; i < Math.max(oldContent.length, newContent.length); i++) {
    if (oldContent[i] !== newContent[i]) diffChars++;
  }
  const pctChanged = Math.round((diffChars / maxLen) * 100);

  if (pctChanged > 40) {
    return `Major rewrite (${pctChanged}% changed)`;
  }

  const parts = [];
  if (linesDiff > 0)
    parts.push(`Added ${linesDiff} line${linesDiff > 1 ? "s" : ""}`);
  else if (linesDiff < 0)
    parts.push(
      `Removed ${Math.abs(linesDiff)} line${Math.abs(linesDiff) > 1 ? "s" : ""}`,
    );

  if (wordsDiff > 0)
    parts.push(`${wordsDiff} word${wordsDiff > 1 ? "s" : ""} added`);
  else if (wordsDiff < 0)
    parts.push(
      `${Math.abs(wordsDiff)} word${Math.abs(wordsDiff) > 1 ? "s" : ""} removed`,
    );

  if (parts.length === 0) {
    if (linesDiff === 0 && wordsDiff === 0) return "Minor edits";
    return "Edited";
  }

  return parts.join(", ");
}

/**
 * Check if changes exceed the threshold for a new version
 */
function shouldCreateVersion(currentContent, lastVersionContent) {
  if (!lastVersionContent) return true;
  if (!currentContent) return false;

  // Character diff threshold: 10+
  let charDiff = 0;
  const maxLen = Math.max(currentContent.length, lastVersionContent.length);
  for (let i = 0; i < maxLen; i++) {
    if (currentContent[i] !== lastVersionContent[i]) charDiff++;
  }
  if (charDiff >= 10) return true;

  // Line diff threshold: 2+
  const oldLines = lastVersionContent.split("\n").filter((l) => l.trim());
  const newLines = currentContent.split("\n").filter((l) => l.trim());
  if (Math.abs(oldLines.length - newLines.length) >= 2) return true;

  return false;
}

const useVersionHistory = ({ storageType, isAuthenticated }) => {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedVersionContent, setSelectedVersionContent] = useState(null);
  const [previousVersionContent, setPreviousVersionContent] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionPanelSongId, setVersionPanelSongId] = useState(null);
  const lastVersionContentRef = useRef({});

  const isCloud = storageType === "database" && isAuthenticated;

  /**
   * Load version list for a song
   */
  const loadVersions = useCallback(
    async (songId) => {
      if (!songId) return;
      setIsLoadingVersions(true);
      try {
        let versionList;
        if (isCloud) {
          versionList = await versionAPI.listVersions(songId);
        } else {
          versionList = await versionIDB.listVersions(songId);
        }
        setVersions(versionList);
      } catch (error) {
        console.error("Failed to load versions:", error);
        setVersions([]);
      } finally {
        setIsLoadingVersions(false);
      }
    },
    [isCloud],
  );

  /**
   * Fetch version content without setting state (for diff loading)
   */
  const fetchVersionContent = useCallback(
    async (songId, versionNumber) => {
      try {
        let version;
        if (isCloud) {
          version = await versionAPI.getVersionContent(songId, versionNumber);
        } else {
          version = await versionIDB.getVersion(songId, versionNumber);
        }
        return version?.content || null;
      } catch (error) {
        console.error("Failed to fetch version content:", error);
        return null;
      }
    },
    [isCloud],
  );

  /**
   * Load full content for a specific version
   */
  const loadVersionContent = useCallback(
    async (songId, versionNumber) => {
      const content = await fetchVersionContent(songId, versionNumber);
      setSelectedVersionContent(content);
      return content;
    },
    [fetchVersionContent],
  );

  /**
   * Create a new version for a song
   */
  const createVersion = useCallback(
    async (songId, title, content, previousContent) => {
      if (!songId || !content) return null;

      const changeSummary = computeChangeSummary(previousContent, content);

      try {
        if (isCloud) {
          const version = await versionAPI.createVersion(
            songId,
            title,
            content,
            changeSummary,
          );
          // Update last known content
          lastVersionContentRef.current[songId] = content;
          return version;
        } else {
          // Local mode
          const maxVer = await versionIDB.getMaxVersionNumber(songId);
          const nextVer = maxVer + 1;

          // Prune if needed (10 for free)
          await versionIDB.pruneVersions(songId, 10);

          const version = await versionIDB.saveVersion(
            songId,
            nextVer,
            title,
            content,
            changeSummary,
          );
          lastVersionContentRef.current[songId] = content;
          return version;
        }
      } catch (error) {
        console.error("Failed to create version:", error);
        return null;
      }
    },
    [isCloud],
  );

  /**
   * Check threshold and create version if warranted
   */
  const maybeCreateVersion = useCallback(
    async (songId, title, content) => {
      let lastContent = lastVersionContentRef.current[songId];

      // If ref is empty (e.g. after page refresh), load latest version from storage
      if (lastContent === undefined || lastContent === null) {
        try {
          let versionList;
          if (isCloud) {
            versionList = await versionAPI.listVersions(songId);
          } else {
            versionList = await versionIDB.listVersions(songId);
          }
          if (versionList.length > 0) {
            const latestVNum =
              versionList[0].versionNumber || versionList[0].version_number;
            let latest;
            if (isCloud) {
              latest = await versionAPI.getVersionContent(songId, latestVNum);
            } else {
              latest = await versionIDB.getVersion(songId, latestVNum);
            }
            if (latest?.content) {
              lastContent = latest.content;
              lastVersionContentRef.current[songId] = lastContent;
            }
          }
        } catch (error) {
          console.error("Failed to load latest version for comparison:", error);
        }
      }

      if (!shouldCreateVersion(content, lastContent)) return null;
      return createVersion(songId, title, content, lastContent);
    },
    [createVersion, isCloud],
  );

  /**
   * Initialize version tracking for a song (set last known content)
   */
  const initSongVersionTracking = useCallback(
    async (songId, currentContent) => {
      lastVersionContentRef.current[songId] = currentContent;
    },
    [],
  );

  /**
   * Restore a version — saves current content as new version first
   */
  const restoreVersion = useCallback(
    async (songId, versionNumber, currentTitle, currentContent) => {
      try {
        // Save current state as a new version before restoring
        await createVersion(songId, currentTitle, currentContent, null);

        // Load the target version's content
        let targetContent;
        if (isCloud) {
          const version = await versionAPI.getVersionContent(
            songId,
            versionNumber,
          );
          targetContent = version?.content;
        } else {
          const version = await versionIDB.getVersion(songId, versionNumber);
          targetContent = version?.content;
        }

        if (!targetContent) {
          throw new Error("Could not load version content");
        }

        // Update tracking
        lastVersionContentRef.current[songId] = targetContent;

        // Reload version list
        await loadVersions(songId);

        return targetContent;
      } catch (error) {
        console.error("Failed to restore version:", error);
        throw error;
      }
    },
    [isCloud, createVersion, loadVersions],
  );

  /**
   * Open the version history panel for a song
   */
  const openVersionPanel = useCallback(
    (songId) => {
      setVersionPanelSongId(songId);
      setSelectedVersion(null);
      setSelectedVersionContent(null);
      setPreviousVersionContent(null);
      loadVersions(songId);
    },
    [loadVersions],
  );

  /**
   * Close the version history panel
   */
  const closeVersionPanel = useCallback(() => {
    setVersionPanelSongId(null);
    setSelectedVersion(null);
    setSelectedVersionContent(null);
    setPreviousVersionContent(null);
    setVersions([]);
  }, []);

  /**
   * Get version count for a song (for showing/hiding the button)
   */
  const getVersionCount = useCallback(
    async (songId) => {
      try {
        if (isCloud) {
          const versionList = await versionAPI.listVersions(songId);
          return versionList.length;
        } else {
          return await versionIDB.countVersions(songId);
        }
      } catch {
        return 0;
      }
    },
    [isCloud],
  );

  return {
    // State
    versions,
    selectedVersion,
    setSelectedVersion,
    selectedVersionContent,
    previousVersionContent,
    setPreviousVersionContent,
    isLoadingVersions,
    versionPanelSongId,

    // Actions
    loadVersions,
    loadVersionContent,
    fetchVersionContent,
    createVersion,
    maybeCreateVersion,
    initSongVersionTracking,
    restoreVersion,
    openVersionPanel,
    closeVersionPanel,
    getVersionCount,

    // Utilities (exported for useSongManager integration)
    shouldCreateVersion,
    computeChangeSummary,
  };
};

export default useVersionHistory;
